import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import Button from 'theme/atoms/Button'
import CheckRadio from 'theme/atoms/CheckRadio'
import Input from 'theme/atoms/Input'
import useTranslations from 'hooks/useTranslations'
import withTranslations from './withTranslations'
import * as t from './types'
import Link from 'theme/atoms/Link'
import Spinner from 'theme/atoms/Spinner'
import useRaffle from './useRaffle'

export function RaffleRegistrationForm(props: t.Props) {
  const t = useTranslations<'organisms-raffle-registration-form'>()

  const $firstName = React.useRef<any>(null)
  const $lastName = React.useRef<any>(null)
  const $phoneNumber = React.useRef<any>(null)
  const $email = React.useRef<any>(null)

  const raffle = useRaffle(props)
  const {
    showCompletionPage,
    email,
    emailError,
    conditionsError,
    newsletterError,
    firstName,
    lastName,
    phoneNumber,
    isFetching,
    completionPageMode,
    acceptCondition,
    acceptNewsletter,
    setEmail,
    setFirstName,
    setLastName,
    setPhoneNumber,
    setAcceptNewsletter,
    setAcceptCondition,
    onSubmit
  } = raffle

  return (
    <Wrapper data-cy-ctx="organisms/RaffleRegistrationForm">
      {isFetching && (
        <div className="spinner-overlay" data-cy-state="show-spinner">
          <Spinner size="big" />
        </div>
      )}
      {!showCompletionPage && (
        <form
          data-cy-state="formular-shown"
          method="POST"
          onSubmit={onSubmit}
          noValidate
          encType="application/x-www-form-urlencoded"
        >
          <div className="raffle-information">
            <div className="headline">{props.headline}</div>
            <div className="description">{props.description}</div>
          </div>
          <div className="input-fields">
            {props.showFirstname && (
              <Input
                type="text"
                inputRef={$firstName}
                data-cy-state="show-firstname"
                placeholder={t.asText($firstName, 'firstname')}
                data-cy-handle="firstname-input"
                value={firstName}
                onChange={(value) => {
                  setFirstName(value)
                }}
                error={''}
              />
            )}
            {props.showLastname && (
              <Input
                type="text"
                inputRef={$lastName}
                data-cy-state="show-lastname"
                placeholder={t.asText($lastName, 'lastname')}
                data-cy-handle="lastname-input"
                value={lastName}
                onChange={(value) => {
                  setLastName(value)
                }}
                error={''}
              />
            )}
            {props.showPhoneNumber && (
              <Input
                type="text"
                inputRef={$phoneNumber}
                data-cy-state="show-phonenumber"
                placeholder={t.asText($phoneNumber, 'phone_number')}
                data-cy-handle="phonenumber-input"
                value={phoneNumber}
                onChange={(value) => {
                  setPhoneNumber(value)
                }}
                error={''}
              />
            )}
            <Input
              type="email"
              inputRef={$email}
              placeholder={t.asText($email, 'email')}
              data-cy-handle="email-input"
              value={email}
              onChange={(value) => {
                setEmail(value)
              }}
              error={emailError}
            />
          </div>

          <div className="raffle-condition">
            <CheckRadio
              label="raffle_condition"
              required
              data-cy-handle="condition-checkbox"
              variation="checkbox"
              checked={acceptCondition}
              onClick={() => setAcceptCondition(!acceptCondition)}
              error={conditionsError}
            >
              {t('raffle_conditions', {
                CONDITIONS: '-',
                __transformers: {
                  CONDITIONS: (_, vars) => (
                    <LinkText>
                      {
                        <Link to={vars.CONDITIONS_link}>
                          {vars.CONDITIONS_label}
                        </Link>
                      }
                    </LinkText>
                  )
                }
              })}
            </CheckRadio>
          </div>
          {props.acceptNewsletterText && (
            <div
              className="newsletter-accepted"
              data-cy-state="show-newsletter-checkbox"
            >
              <CheckRadio
                label={props.acceptNewsletterText}
                data-cy-handle="newsletter-checkbox"
                required={props.newsletterRequired}
                variation="checkbox"
                checked={acceptNewsletter}
                onClick={() => setAcceptNewsletter(!acceptNewsletter)}
                error={newsletterError}
              />
            </div>
          )}

          <Button
            variation="special"
            data-cy-handle="submit-button"
            label={props.submitButtonText}
            submit
          ></Button>
        </form>
      )}
      {showCompletionPage === 'success' && (
        <div className="success-screen" data-cy-state="success-shown">
          <div className="headline">{props.successHeadline}</div>
          <div className="description">{props.successText}</div>
          {completionPageMode === 'MAIL_TRIGGER' && (
            <div
              data-cy-state="success-shown-mail-triggered"
              className="description"
            >
              {props.successTextMailTriggered}
            </div>
          )}
        </div>
      )}
      {showCompletionPage === 'failure' && (
        <div className="failure-screen" data-cy-state="failure-shown">
          <div className="headline">{props.failureHeadline}</div>
          <div className="description">{props.failureText}</div>
        </div>
      )}
    </Wrapper>
  )
}

export default withTranslations(RaffleRegistrationForm)

const Wrapper = styled.div`
  ${theme.ty('rc-base')}
  position: relative;
  padding: ${theme.spacing('xs')};
  > .spinner-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: ${theme.zIndex.theme_organisms_raffleregistrationform};
    /* background: rgba(0, 0, 0, 0); */
  }
  > form {
    padding: ${theme.spacing('xs')};
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    > .raffle-information {
      margin-bottom: ${theme.spacing('xs')};
      > .headline {
        ${theme.ty('rc-3xl')}
        margin-bottom: ${theme.spacing('xs')};
      }
      > .description {
      }
    }
    > .input-fields {
      display: flex;
      flex-direction: column;
      row-gap: 10px;
      margin-bottom: ${theme.spacing('xs')};
    }
    > .raffle-condition {
      display: flex;
      > .condition-link {
        margin-left: ${theme.spacing('xxs')};
        color: ${theme.colors.primary};
      }
    }
    > .newsletter-condition {
      display: flex;
    }
  }

  > .success-screen,
  > .failure-screen {
    padding: ${theme.spacing('xs')};
    > .headline {
      ${theme.ty('rc-3xl')}
      margin-bottom: ${theme.spacing('xs')};
    }
    > .description {
    }
  }
`

const LinkText = styled.span`
  text-decoration: underline;
`
